import { useState, useEffect } from "react";
import axios from "axios";
import { Calculator, Home, Swords } from "lucide-react";
import { API } from "../../context/AuthContext";

const card = "bg-[#131f3a] border border-white/5 rounded-2xl p-5 space-y-3";
const inputCls = "w-full bg-gray-950 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-100 focus:outline-none focus:border-[#f59e0b]/50";
const labelCls = "text-[9px] font-mono text-gray-400 uppercase";
const fmt = (n) => (isFinite(n) ? n.toLocaleString("en-US", { style: "currency", currency: "USD" }) : "—");

const FREQ = { weekly: { m: 52 / 12, label: "Weekly (×52 ÷12)" }, biweekly: { m: 26 / 12, label: "Bi-Weekly (×26 ÷12)" }, semimonthly: { m: 2, label: "Semi-Monthly (×2)" }, monthly: { m: 1, label: "Monthly (×1)" } };

function IncomeConverter() {
  const [gross, setGross] = useState("");
  const [freq, setFreq] = useState("biweekly");
  const [voluntary, setVoluntary] = useState("");
  const g = parseFloat(gross) || 0;
  const v = parseFloat(voluntary) || 0;
  const monthly = g * FREQ[freq].m;
  const baseline = monthly + v * FREQ[freq].m;
  return (
    <div className={card} data-testid="tool-income-converter">
      <h4 className="text-xs font-bold text-white uppercase tracking-wide font-display flex items-center gap-2"><Calculator className="w-3.5 h-3.5 text-[#f59e0b]" /> Monthly Income Converter</h4>
      <p className="text-[10px] text-gray-400">DissoMaster-ready monthly equivalents using statutory multipliers.</p>
      <div className="grid grid-cols-2 gap-2">
        <div><label className={labelCls}>Gross pay per period</label><input value={gross} onChange={(e) => setGross(e.target.value)} type="number" data-testid="income-gross-input" className={inputCls} placeholder="4200" /></div>
        <div>
          <label className={labelCls}>Pay frequency</label>
          <select value={freq} onChange={(e) => setFreq(e.target.value)} data-testid="income-freq-select" className={inputCls}>
            {Object.entries(FREQ).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
          </select>
        </div>
      </div>
      <div><label className={labelCls}>Voluntary deductions per period (401k/HSA add-back)</label><input value={voluntary} onChange={(e) => setVoluntary(e.target.value)} type="number" data-testid="income-voluntary-input" className={inputCls} placeholder="570" /></div>
      <div className="grid grid-cols-2 gap-2 text-center">
        <div className="bg-gray-950 rounded-xl p-3 border border-white/5"><div className={labelCls}>Monthly Gross</div><div className="text-[#2dd4bf] font-bold text-sm font-display" data-testid="income-monthly-result">{fmt(monthly)}</div></div>
        <div className="bg-gray-950 rounded-xl p-3 border border-[#f59e0b]/20"><div className={labelCls}>Support Baseline (w/ add-back)</div><div className="text-[#f59e0b] font-bold text-sm font-display" data-testid="income-baseline-result">{fmt(baseline)}</div></div>
      </div>
    </div>
  );
}

function MooreMarsden() {
  const [f, setF] = useState({ price: "", fmvMarriage: "", fmvNow: "", commPrincipal: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const price = parseFloat(f.price) || 0;
  const cp = parseFloat(f.commPrincipal) || 0;
  const apprec = (parseFloat(f.fmvNow) || 0) - (parseFloat(f.fmvMarriage) || 0);
  const pct = price > 0 ? cp / price : 0;
  const commInterest = cp + pct * apprec;
  return (
    <div className={card} data-testid="tool-moore-marsden">
      <h4 className="text-xs font-bold text-white uppercase tracking-wide font-display flex items-center gap-2"><Home className="w-3.5 h-3.5 text-[#2dd4bf]" /> Moore/Marsden Calculator</h4>
      <p className="text-[10px] text-gray-400">Community interest in separate-property real estate paid down with community funds.</p>
      <div className="grid grid-cols-2 gap-2">
        <div><label className={labelCls}>Purchase price</label><input value={f.price} onChange={set("price")} type="number" data-testid="mm-price-input" className={inputCls} placeholder="400000" /></div>
        <div><label className={labelCls}>Community principal paydown</label><input value={f.commPrincipal} onChange={set("commPrincipal")} type="number" data-testid="mm-comm-input" className={inputCls} placeholder="60000" /></div>
        <div><label className={labelCls}>FMV at marriage</label><input value={f.fmvMarriage} onChange={set("fmvMarriage")} type="number" data-testid="mm-fmv-marriage-input" className={inputCls} placeholder="450000" /></div>
        <div><label className={labelCls}>FMV now / at trial</label><input value={f.fmvNow} onChange={set("fmvNow")} type="number" data-testid="mm-fmv-now-input" className={inputCls} placeholder="700000" /></div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-gray-950 rounded-xl p-3 border border-white/5"><div className={labelCls}>Community %</div><div className="text-gray-200 font-bold text-sm font-display" data-testid="mm-pct-result">{(pct * 100).toFixed(2)}%</div></div>
        <div className="bg-gray-950 rounded-xl p-3 border border-white/5"><div className={labelCls}>Apprec. share</div><div className="text-gray-200 font-bold text-sm font-display" data-testid="mm-apprec-result">{fmt(pct * apprec)}</div></div>
        <div className="bg-gray-950 rounded-xl p-3 border border-[#2dd4bf]/20"><div className={labelCls}>Community interest</div><div className="text-[#2dd4bf] font-bold text-sm font-display" data-testid="mm-interest-result">{fmt(commInterest)}</div></div>
      </div>
      <p className="text-[9px] text-gray-500">Formula: CP principal + (CP principal ÷ purchase price) × marital appreciation. Verify with counsel — not legal advice.</p>
    </div>
  );
}

function ConflictMatrix({ caseId }) {
  const [rfos, setRfos] = useState([]);
  useEffect(() => {
    axios.get(`${API}/intelligence`, { params: { case_id: caseId } })
      .then((res) => setRfos(res.data.profiles.filter((p) => p.doc_category === "rfo")))
      .catch(() => {});
  }, [caseId]);
  const side = (s) => rfos.filter((p) => p.spouse_identity === s);
  const Col = ({ title, list, cls }) => (
    <div className="space-y-2">
      <div className={`text-[10px] font-mono font-bold uppercase text-center py-1 rounded border ${cls}`}>{title}</div>
      {list.length === 0 ? (
        <p className="text-[10px] text-gray-500 text-center italic">No RFO filed</p>
      ) : list.map((p) => (
        <div key={p.profile_id} className="bg-gray-950 border border-white/5 rounded-lg p-2.5 space-y-1">
          <p className="text-[10px] text-gray-400 font-mono">{p.fields?.Filing_Metadata || ""}</p>
          <p className="text-[11px] text-gray-200"><span className="text-gray-500 font-mono text-[9px] uppercase">Wants: </span>{p.fields?.Relief_Sought || "Unknown/Unstated in Text"}</p>
          <p className="text-[10px] text-gray-400 leading-relaxed">{(p.fields?.Declaration_Facts || "").slice(0, 300)}</p>
        </div>
      ))}
    </div>
  );
  return (
    <div className={card} data-testid="tool-conflict-matrix">
      <h4 className="text-xs font-bold text-white uppercase tracking-wide font-display flex items-center gap-2"><Swords className="w-3.5 h-3.5 text-rose-400" /> RFO Conflict Matrix</h4>
      <p className="text-[10px] text-gray-400">Side-by-side: what Husband wants vs what Wife wants, from ingested Requests for Order.</p>
      <div className="grid grid-cols-2 gap-3">
        <Col title="HUSBAND" list={side("husband")} cls="text-sky-400 border-sky-500/30 bg-sky-500/10" />
        <Col title="WIFE" list={side("wife")} cls="text-orange-400 border-orange-500/30 bg-orange-500/10" />
      </div>
    </div>
  );
}

export default function Toolbox({ caseId }) {
  return (
    <div className="space-y-4" data-testid="toolbox-panel">
      <IncomeConverter />
      <MooreMarsden />
      <ConflictMatrix caseId={caseId} />
    </div>
  );
}
